import { AbilityId, type CharacterKind } from '@/components'

/**
 * The whole balance table lives here. Every number a designer might want to
 * tweak is in this file and nowhere else — the handlers read it, the HUD reads
 * it, the audio cues read it.
 */
export interface AbilityDef {
  id: number
  name: string
  /** One line explaining what it does, shown on the HUD and in the menu. */
  description: string
  keyboardLabel: string
  padLabel: string
  cooldown: number
  maxCharges: number
  /** Telegraph before the effect lands. */
  windup: number
  /** Window during which the effect is live. */
  active: number
  /** Lockout after the effect, during which no other ability may start. */
  recover: number
  /** Held rather than tapped (Brace). */
  hold: boolean
  /**
   * Forward speed the caster is driven at during the active phase, m/s.
   * This is where the cost of an attack lives: a lunge plants you inside your
   * opponent's reach, so a whiff is punishable. See docs/PHYSICS.md §6.3.
   */
  lunge: number
  /** Input authority during recovery, 0..1. */
  recoverAuthority: number
  color: number
}

/**
 * Launch parameters, split into a flat base and a part that grows with the
 * victim's instability — the same base/scaling split Smash uses, so one move can
 * be a positioning tool early and a finisher late. See docs/PHYSICS.md §6.2.
 */
export interface LaunchSpec {
  base: number
  scaling: number
}

/**
 * Launch speed of a hit, in a world `worldScale` times the authored size (see
 * `worldScale` in resources.ts).
 *
 * Every number that answers "how far" has to move with the platform, or the
 * game changes character every time a map grows. It did: the arenas were
 * enlarged by 25–30 % and then again up to 1.7× on request, and nothing about
 * movement or knockback was revisited either time, so a hit that used to cross
 * a third of the platform ended up crossing a fifth of it. See CLAUDE.md §8.
 *
 * Distance under the launch model grows with the square of the speed, so the
 * speed scales with the square root of the size and the *fraction of the arena*
 * a hit carries you stays constant. Walking scales linearly, for the same
 * reason: crossing the platform must take the same few seconds at any size.
 */
export function launchSpeed(spec: LaunchSpec, instability: number, worldScale: number): number {
  return (spec.base + spec.scaling * (instability / 100)) * Math.sqrt(worldScale)
}

/**
 * A launch is a low, skipping arc — a stone across water — not a slide.
 *
 * Horizontal speed is not damped in the air at all; it is lost only on contact
 * with the slab, together with the bounce. The number of bounces is therefore
 * proportional to the impulse, which makes the strength of a hit something you
 * can *count* rather than merely infer from distance. See docs/PHYSICS.md §6.2.
 */
export const LAUNCH_BRAKE = 45
/** Vertical speed as a fraction of launch speed. Low on purpose. */
export const LAUNCH_LIFT = 0.24
/**
 * Gravity during a launch — heavier than the 22 m/s² a falling body gets.
 *
 * The two cannot be the same number. A skipping arc loses horizontal speed only
 * on contact, so the lighter the gravity the longer the hops and the fewer the
 * contacts: at 22 the measured travel was 12 m for a fresh Push and 35.7 m at
 * full instability, on platforms 28–36 m across. Every strong hit was an instant
 * kill from anywhere and the launch table in docs/PHYSICS.md §6.2 was fiction.
 * At 34 the same hits measure 7.4 m and 21 m, which is what the table asks for.
 */
export const LAUNCH_GRAVITY = 34
/** Vertical restitution on hitting the slab. */
export const LAUNCH_BOUNCE = 0.42
/**
 * Horizontal speed kept through each ground contact. This is the only place a
 * launch sheds speed before it settles, so it, gravity and the lift together
 * decide the whole distance curve.
 */
export const LAUNCH_SKID = 0.55
/**
 * Below this vertical speed the fighter stops bouncing and slides to a halt.
 *
 * Set at 1.2 rather than the 2.0 the spec sketched, because 2.0 measured out at
 * exactly one contact for a fresh Push and two for the hardest hit in the game:
 * too coarse to count. At 1.2 a base Push skips twice and a full-instability
 * one three times, so the number of impacts you hear is the strength of the
 * blow. Verified with the launch probe in `tools/simcheck.ts`.
 */
export const LAUNCH_SETTLE = 1.2
/**
 * How far the victim may rotate their own trajectory, in radians (±18°, the
 * Melee figure). Bounded influence keeps agency without letting the victim
 * decide the outcome — which is what the old model did, to the tune of 11×.
 */
export const DI_MAX_ANGLE = 0.314

/**
 * Simulates the skipping arc to get its total time and distance.
 *
 * Closed form would need the bounce series; iterating the same rules the
 * movement system uses is shorter, exact, and cannot drift away from the
 * implementation. Used by the design tables, the AI and the HUD.
 */
export function launchProfile(speed: number): { duration: number; distance: number } {
  let vx = speed
  let vy = speed * LAUNCH_LIFT
  let t = 0
  let x = 0
  // The simulation's own step. A finer one here would be more accurate and
  // less useful: what this function has to predict is what the game will
  // actually do, integration error included.
  const dt = 1 / 60

  // Airborne phase: bounce until the fighter settles.
  while (vy > LAUNCH_SETTLE || t === 0) {
    let y = 0
    let bouncing = true
    while (bouncing) {
      vy -= LAUNCH_GRAVITY * dt
      y += vy * dt
      x += vx * dt
      t += dt
      if (y <= 0) {
        vy = -vy * LAUNCH_BOUNCE
        vx *= LAUNCH_SKID
        bouncing = false
      }
      if (t > 6) return { duration: t, distance: x }
    }
  }

  // Grounded phase: constant deceleration to a stop.
  t += vx / LAUNCH_BRAKE
  x += (vx * vx) / (2 * LAUNCH_BRAKE)
  return { duration: t, distance: x }
}

/**
 * Impulses after the second playtest.
 *
 * The complaint was that Push and Wave barely moved anyone while Dash felt
 * strong — and the reason was structural, not a matter of a few percent. A dash
 * lands its own impulse *plus* a body collision at 17 m/s closing speed, so it
 * was effectively hitting twice. Push and Wave had to clear that bar on their
 * own, and the knockback drag came down from 4.0 to 3.0 so a hit keeps sliding
 * long enough to read as a hit.
 *
 * At drag 3.0 a fresh Push now carries about 7 m, and roughly 15 m at full
 * instability. That only works on arenas with room to travel, which is why the
 * platforms grew at the same time.
 */
/**
 * The plasma bolt Push throws. See docs/PHYSICS.md §6.2а.
 *
 * Flatter gravity than a fighter so the arc is long and low, a generous floor
 * bounce so it skips rather than lands, and near-lossless reflection off
 * obstacles so a ricochet is worth setting up.
 */
/**
 * The plasma bolt, retuned to actually skip.
 *
 * At 17 m/s with gravity 14 the first contact with the slab came after 0.6 s
 * and 10 m — beyond the range fights happen at, so in practice the bolt never
 * bounced once and simply drifted across the arena. Both halves of that are
 * fixed together, because they are one shape: the speed is tripled, and the arc
 * is made far tighter so the higher speed produces *more* contacts rather than
 * fewer. Hops now run about 8 m, 5 m, 3 m with an apex of ~0.3 m — a stone
 * skipping across water, which is what was asked for.
 */
export const BOLT = {
  speed: 50,
  lift: 7.5,
  gravity: 95,
  /** Vertical restitution and horizontal retention on hitting the slab. */
  bounce: 0.62,
  skid: 0.9,
  /** Speed kept through a mirror reflection off a prop. */
  reflect: 0.92,
  /**
   * Wider than a ball, because it is not a ball: Push throws a crescent, so it
   * sweeps a front rather than threading a needle. A 0.45 m pellet at 50 m/s
   * demanded pixel aim on a target 1.5 m across.
   */
  radius: 0.85,
  /**
   * Half the width of the front, in metres.
   *
   * Push does not throw a pellet, it throws a **wave with an object's edge**: a
   * boomerang-shaped front 3.4 m across that sweeps everything in its lane. A
   * round projectile — even a wide one — still has to be threaded through a gap
   * at a target 1.5 m wide, and aiming it by facing alone was miserable.
   *
   * The hit test matches the shape: a swept rectangle of this half-width, not a
   * circle, so the crescent connects with whatever its arc actually crosses.
   */
  halfWidth: 1.7,
  /**
   * Height at which the crescent skips off the slab.
   *
   * Deliberately not the hit radius. Tying the two together meant that widening
   * the crescent put its skip height above its own spawn height, so it bounced
   * on every single step and lost 10 % of its speed each time — it died two
   * metres from the caster and the shot did nothing at all. Caught by the frame
   * strip in `shotcheck`, which is exactly the artefact that did not exist when
   * this model was first declared verified.
   */
  floorY: 0.45,
  /** Height the crescent leaves the hand at. */
  spawnY: 1.05,
  /**
   * 1.4 s covers 70 m at speed, which outlasts the widest arena; the bolt
   * almost always dies at the edge instead. Any longer is just an entity
   * nobody can see still being simulated.
   */
  life: 1.4,
  selfGrace: 0.12,
} as const

/**
 * The launch table, anchored to the arena instead of to a spreadsheet.
 *
 * The previous numbers came from a table in docs/PHYSICS.md and were verified
 * against that table — which is exactly the wrong direction. Measured against
 * the platform they were built on, a clean Push moved a fighter 6.8 m: 38 % of
 * the radius, and the victim walked it back in 0.98 s. **The strongest attack
 * in the game moved you less than a second of walking.** That is the whole
 * complaint, in one number.
 *
 * These are chosen from what a hit has to be worth, and then converted to
 * speeds through `launchProfile`:
 *
 * | Приём            | доля радиуса @0 % | @100 % |
 * |------------------|-------------------|--------|
 * | Толчок           | 75 %              | 150 %  |
 * | Волна (центр)    | 60 %              | 105 %  |
 * | Волна (кромка)   | 50 %              |  90 %  |
 * | Рывок            | 40 %              |  70 %  |
 * | Воронка          | 90 %              | 165 %  |
 *
 * The spread from instability is deliberately held near **2×**, not the 3× it
 * was: the victim's damage is not on the attacker's screen, and an outcome that
 * swings threefold on a number you cannot see reads as randomness. The player
 * said so directly — «иногда работает нормально сильно, но я не понял почему».
 */
export const PUSH = {
  coneDegrees: 105,
  range: 3.1,
  /**
   * Launched along the ATTACKER'S FACING, not along the line between bodies.
   * The old direction meant that turning changed nothing — measured: rotating
   * the attacker ±50° left the victim's bearing at exactly 90.00° — so "aiming"
   * was really sub-frame positioning, and the game drew a cone that lied about
   * it. See docs/PHYSICS.md §6.1.
   */
  launch: { base: 29, scaling: 12 } as LaunchSpec,
  instability: 11,
  /** Also shoves props, at a reduced rate. */
  propPower: 12,
} as const

export const DASH = {
  duration: 0.18,
  speed: 17,
  launch: { base: 21, scaling: 8 } as LaunchSpec,
  instability: 8,
  /** Knockback multiplier while dashing — light super armour, not immunity. */
  knockbackResist: 0.35,
} as const

/**
 * Wave's job is the edge, not the clinch.
 *
 * It used to be strictly worse than Push in a duel: less displacement at the
 * range fights actually happen, on a seven-second cooldown, behind a telegraph.
 * The fix is not more raw power but a flatter falloff — the ring now hits
 * nearly as hard at its rim as at its centre, which is the one thing Push
 * cannot do: shove someone who is already backed up against the void.
 */
export const WAVE = {
  maxRadius: 7,
  expandSpeed: 20,
  /** Radial by design — being a ring is the whole role of this ability. */
  launch: { base: 26, scaling: 9 } as LaunchSpec,
  launchEdge: { base: 24, scaling: 8 } as LaunchSpec,
  instability: 6,
  propPower: 14,
} as const

export const BRACE = {
  /**
   * Long enough to be a real decision, short enough not to own the round.
   * At 4 s with a 5 s cooldown counted from release, the stance was up 44 % of
   * the time — and at ×0.2 knockback plus braced friction it was not resistance
   * but immunity.
   */
  maxHold: 3.2,
  /**
   * The stance stays up this long even if the button is released at once.
   *
   * Without it a tap put the shield up and took it down inside the same frame,
   * so the ability answered nothing and looked broken: you pressed, something
   * flashed, and you were hit anyway. A floor also makes the cost real — you
   * cannot feint a stance for free — which is what a defensive option needs to
   * be a decision rather than a reflex.
   */
  minHold: 1,
  knockbackMul: 0.32,
  speedMul: 0.15,
  /**
   * Instability bleeds off this much faster while braced. At ×4 over a 4 s hold
   * the stance shed 40 instability — more than four landed Pushes add — making
   * the best defence also the best heal.
   */
  decayMul: 2,
  /**
   * Impulse that shatters the stance, compared *after* instability scaling.
   *
   * At 26 against the raw value it was dead code — nothing in the game could
   * reach it. Measured against the scaled impulse it becomes the most
   * interesting rule in the kit: a fresh fighter's Brace holds a Push (21), but
   * once they are past ~45 % instability the same Push (21 × 1.54 = 32) goes
   * straight through it. Being battered stops the stance from saving you.
   */
  breakThreshold: 30,
  breakStun: 0.6,
} as const

export const VORTEX = {
  /**
   * Planted under the caster's own feet. Throwing it 7.5 m ahead made it
   * impossible to tell where it would land, so the ability read as random.
   * Centred on you it is legible, and it becomes a genuine commitment: you are
   * standing in your own trap and have to dash out or ride the burst.
   */
  arming: 0.4,
  /**
   * Short and violent. At 11 m/s² against a player who accelerates at 45 the
   * pull was a suggestion, so the victim simply walked out and the caster ate
   * their own burst. It now genuinely holds you for a moment.
   */
  pullDuration: 0.85,
  radius: 5.2,
  pull: 30,
  /**
   * Fastest a caught fighter may still walk away from the centre, m/s.
   *
   * The pull is a **grip**, not an acceleration: a fighter accelerates at 62
   * m/s² and the field pulled at 30, so walking out beat being dragged in by
   * two to one and the anomaly held nobody. Capping the outgoing component
   * instead means the trap works while leaving the victim their sideways
   * movement and their abilities. See docs/PHYSICS.md §6.6.
   */
  gripSpeed: 2.5,
  launch: { base: 32, scaling: 12 } as LaunchSpec,
  instability: 14,
} as const

/**
 * Per-fighter kits.
 *
 * Everyone has the same five buttons — a game where one character lacks an
 * escape is not a roster, it is a trap — but each runs them at their own
 * tempo and weight. The design rule is that every kit spends the same budget:
 * whatever a character gains in one number they give back in another, and no
 * kit is stronger in every column.
 *
 * `cooldown` and `charges` are absolute per-ability overrides; everything else
 * is a multiplier on the shared table above. Balance is not asserted here — it
 * is measured: `npm run simcheck` plays every character against every other
 * with identical AI and reports win rates, and fails if any fighter lands
 * outside 30–70 %.
 */
export interface FighterKit {
  pushPower: number
  boltSpeed: number
  wavePower: number
  waveRadius: number
  dashPower: number
  vortexPower: number
  vortexRadius: number
  braceMul: number
  braceHold: number
  cooldown: Partial<Record<AbilityId, number>>
  charges: Partial<Record<AbilityId, number>>
}

const NEUTRAL: FighterKit = {
  pushPower: 1,
  boltSpeed: 1,
  wavePower: 1,
  waveRadius: 1,
  dashPower: 1,
  vortexPower: 1,
  vortexRadius: 1,
  braceMul: 1,
  braceHold: 1,
  cooldown: {},
  charges: {},
}

export const FIGHTER_KITS: Record<CharacterKind, FighterKit> = {
  // Стены не толкают — они держат. Сильнейший толчок и лучшая стойка, самая
  // медленная перезарядка и один заряд рывка.
  guard: {
    ...NEUTRAL,
    pushPower: 1.08,
    braceMul: 0.72,
    braceHold: 1.35,
    dashPower: 0.9,
    cooldown: { [AbilityId.Push]: 1.15, [AbilityId.Brace]: 6 },
    charges: { [AbilityId.Dash]: 1 },
  },
  // Мелкий и назойливый: бьёт часто и слабо, уходит из любой ситуации.
  beetle: {
    ...NEUTRAL,
    pushPower: 0.9,
    boltSpeed: 1.15,
    wavePower: 0.9,
    braceMul: 1.2,
    braceHold: 0.75,
    cooldown: { [AbilityId.Push]: 1.05, [AbilityId.Dash]: 2.4 },
    charges: { [AbilityId.Dash]: 3 },
  },
  // Держит дистанцию: быстрый дальний снаряд и широкая, но слабая волна.
  crane: {
    ...NEUTRAL,
    pushPower: 1.02,
    boltSpeed: 1.3,
    wavePower: 0.88,
    waveRadius: 1.3,
    braceMul: 1.15,
    cooldown: { [AbilityId.Wave]: 5.5 },
  },
  // Всё медленное и тяжёлое. Попал — вынес; промахнулся — стоишь.
  brick: {
    ...NEUTRAL,
    pushPower: 1.08,
    boltSpeed: 0.85,
    wavePower: 1.12,
    dashPower: 1.1,
    braceMul: 0.82,
    cooldown: { [AbilityId.Push]: 1.3, [AbilityId.Wave]: 8 },
    charges: { [AbilityId.Dash]: 1 },
  },
  // Ловушечник: воронка почти вдвое чаще, но бьёт он слабее всех.
  jester: {
    ...NEUTRAL,
    pushPower: 1.02,
    vortexPower: 1.05,
    vortexRadius: 1.15,
    braceMul: 1.3,
    braceHold: 0.8,
    cooldown: { [AbilityId.Vortex]: 7, [AbilityId.Dash]: 2.6 },
    charges: { [AbilityId.Dash]: 3 },
  },
  // Круглый и тяжёлый: волна вокруг себя — его главный приём.
  blob: {
    ...NEUTRAL,
    pushPower: 0.96,
    wavePower: 1.06,
    waveRadius: 1.15,
    dashPower: 1.1,
    braceMul: 0.95,
    cooldown: { [AbilityId.Wave]: 6.5, [AbilityId.Push]: 1.15 },
  },
}

export const ABILITIES: AbilityDef[] = [
  {
    id: AbilityId.Push,
    name: 'ТОЛЧОК',
    description: 'Удар вперёд вблизи. Основная атака',
    keyboardLabel: 'ПРОБЕЛ',
    padLabel: 'A',
    cooldown: 1.1,
    maxCharges: 1,
    // 50 ms to impact, inside Swink's 100 ms threshold for real-time control.
    windup: 0.05,
    active: 0.12,
    // The cost of a miss lives here: 280 ms nearly rooted, having just lunged
    // into your opponent's reach.
    recover: 0.28,
    hold: false,
    lunge: 8,
    recoverAuthority: 0.15,
    color: 0xffd27a,
  },
  {
    id: AbilityId.Dash,
    name: 'РЫВОК',
    description: 'Бросок вперёд. Таран и спасение с края',
    keyboardLabel: 'L-SHIFT',
    padLabel: 'B',
    cooldown: 3,
    maxCharges: 2,
    windup: 0,
    active: DASH.duration,
    recover: 0.1,
    hold: false,
    lunge: 0,
    recoverAuthority: 0.5,
    color: 0x9fe8ff,
  },
  {
    id: AbilityId.Wave,
    name: 'ВОЛНА',
    description: 'Кольцо во все стороны. Отбрасывает всех вокруг',
    keyboardLabel: 'Q',
    padLabel: 'X',
    cooldown: 7,
    maxCharges: 1,
    windup: 0.3,
    active: 0.06,
    recover: 0.32,
    hold: false,
    lunge: 0,
    recoverAuthority: 0.2,
    color: 0x8fff3c,
  },
  {
    id: AbilityId.Brace,
    name: 'СТОЙКА',
    description: 'Держать: почти не сдвинуть, но и не идти',
    keyboardLabel: 'E',
    padLabel: 'LT',
    cooldown: 5,
    maxCharges: 1,
    windup: 0.05,
    active: BRACE.maxHold,
    recover: 0.1,
    hold: true,
    lunge: 0,
    recoverAuthority: 0.4,
    color: 0xc8c2b0,
  },
  {
    id: AbilityId.Vortex,
    name: 'ВОРОНКА',
    description: 'Ловушка вдалеке: стягивает, потом взрывается',
    keyboardLabel: 'R',
    padLabel: 'Y',
    cooldown: 13,
    maxCharges: 1,
    windup: 0.22,
    active: 0.06,
    recover: 0.34,
    hold: false,
    lunge: 0,
    recoverAuthority: 0.25,
    color: 0xb066ff,
  },
]

export function abilityDef(id: number): AbilityDef {
  return ABILITIES[id]
}

/** How far knockback scales with instability: ×1 at 0 %, ×2.2 at 100 %. */
export const INSTABILITY_KNOCKBACK_SCALE = 0.012
/** Instability bleeds off at this rate once the grace period has passed. */
export const INSTABILITY_DECAY = 2.5
export const INSTABILITY_GRACE = 2
/** Body-to-body collisions add a token amount, so ramming is never free. */
export const BUMP_INSTABILITY = 2
